import { Link } from 'react-router-dom';
import { MAIN_SITE_URL } from '@/mocks/categories';
import type { ArticleData } from '@/mocks/articles';

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  return (
    <article className="pt-16">
      <header className="bg-background-100 py-16 md:py-20">
        <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags.map((tag) => (
              <span
                key={tag.label}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  tag.color === 'primary'
                    ? 'bg-primary-100 text-primary-700'
                    : tag.color === 'accent'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}
              >
                {tag.label}
              </span>
            ))}
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            {article.description}
          </p>
        </div>
      </header>

      <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10 py-12 md:py-16">
        {article.heroImage && (
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full h-auto object-cover rounded-2xl"
            />
          </div>
        )}

        {article.sections.map((section, idx) => (
          <section key={idx} className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
              {section.heading}
            </h2>
            {section.content.map((paragraph, pIdx) => (
              <p key={pIdx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </section>
        ))}

        {article.comparison && (
          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
              {article.comparison.heading}
            </h2>
            <div className="overflow-x-auto bg-white rounded-xl border border-background-200/70">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/50 bg-background-50">
                    <th className="text-left px-5 md:px-6 py-3.5 text-xs md:text-sm font-semibold text-foreground-700 whitespace-nowrap">Aspect</th>
                    <th className="text-left px-5 md:px-6 py-3.5 text-xs md:text-sm font-semibold text-foreground-500 whitespace-nowrap">{article.comparison.leftLabel}</th>
                    <th className="text-left px-5 md:px-6 py-3.5 text-xs md:text-sm font-semibold text-accent-700 whitespace-nowrap">{article.comparison.rightLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {article.comparison.rows.map((row) => (
                    <tr key={row.aspect} className="border-b border-background-100 last:border-0 hover:bg-background-50/50 transition-colors">
                      <td className="px-5 md:px-6 py-3 text-xs md:text-sm font-medium text-foreground-800 whitespace-nowrap">{row.aspect}</td>
                      <td className="px-5 md:px-6 py-3 text-xs md:text-sm text-foreground-500">{row.left}</td>
                      <td className="px-5 md:px-6 py-3 text-xs md:text-sm font-medium text-accent-700">{row.right}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {article.faqs.length > 0 && (
          <section className="mb-14 md:mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq) => (
                <div key={faq.question} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="font-heading text-sm md:text-base text-foreground-900 mb-2">{faq.question}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="border-t border-background-200 pt-12">
          <div className="bg-gradient-to-r from-primary-50 via-accent-50 to-secondary-50 rounded-2xl p-8 md:p-10 text-center">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
              Ready to Start Exploring?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-6">
              Dive into the organized world of spreadsheet shopping. Browse categories, compare products, and discover your next favorite find.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/"
                className="px-7 py-3 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="px-7 py-3 bg-white text-primary-600 font-semibold text-sm rounded-full border border-primary-200 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
              >
                VigorBuy Spreadsheet <i className="ri-arrow-right-up-line ml-1 text-xs"></i>
              </a>
            </div>
          </div>
        </section>

        <div className="mt-10 pt-8 border-t border-background-200/50">
          <h3 className="font-heading text-base text-foreground-700 mb-4">Explore More Articles</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/" className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Homepage
            </Link>
            <Link to="/vigorbuy-spreadsheet-guide" className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Complete Guide
            </Link>
            <Link to="/articles/vigorbuy-spreadsheet-sneakers" className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Sneakers Guide
            </Link>
            <Link to="/articles/vigorbuy-spreadsheet-hoodies" className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Hoodies Guide
            </Link>
            <Link to="/articles/vigorbuy-spreadsheet-safety" className="px-4 py-2 bg-background-100 rounded-lg text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
              Safety Guide
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}